import React, { useState, useEffect, useRef } from "react";
import { 
    FaClipboardList, 
    FaClock, 
    FaChartBar, 
    FaGift, 
    FaHeart, 
    FaExclamationTriangle 
  } from "react-icons/fa";
import {
    Chart,
    LineController,
    LineElement,
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    ArcElement,
    Tooltip,
    Legend,
} from "chart.js";

Chart.register(
    LineController,
    LineElement,
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    ArcElement,
    Tooltip,
    Legend
);

export default function KpiCardsOffcanvas() {
    const [openId, setOpenId] = useState(null);

    const kpis = [
        {
            id: "registration",
            title: "Total Pregnant Women Registered",
            value: 15430,
            sub: "1st Trimester: 10,240",
            icon: FaClipboardList,
            color: "from-blue-500 to-blue-600",
        },
        {
            id: "early",
            title: "Early ANC (<12 weeks)",
            value: 9820,
            sub: "ASHA Identified: 6100",
            icon: FaClock,
            color: "from-purple-500 to-purple-600",
        },
        {
            id: "coverage",
            title: "ANC1 / ANC4 / ANC6",
            value: "2430 / 1990 / 1360",
            sub: "Missed: 18.1%",
            icon: FaChartBar,
            color: "from-green-500 to-green-600",
        },
        {
            id: "package",
            title: "Complete ANC Package",
            value: 11900,
            sub: "IFA: 13,000",
            icon: FaGift,
            color: "from-amber-500 to-amber-600",
        },
        {
            id: "anemia",
            title: "Anemia Cases",
            value: 4120,
            sub: "Severe: 520",
            icon: FaHeart,
            color: "from-red-500 to-red-600",
        },
        {
            id: "hrp",
            title: "High-Risk Pregnancies",
            value: 1320,
            sub: "Referred: 1,100",
            icon: FaExclamationTriangle,
            color: "from-rose-500 to-rose-600",
        },
    ];

    const activeKpi = kpis.find((x) => x.id === openId);

    return (
        <div className="h-screen bg-gray-100 overflow-hidden">
            {/* HEADER */}
            <header className="h-[8vh] bg-white shadow-sm flex items-center px-6 justify-between">
                <div>
                    <h1 className="text-lg font-semibold">Maternal ANC Dashboard</h1>
                    <p className="text-xs text-gray-500">KPI Cards with Chart.js Drilldown</p>
                </div>
                <p className="text-xs text-gray-500">Last updated: Today</p>
            </header>

            {/* KPI GRID */}
            <div className="h-[92vh] p-6 grid grid-cols-6 gap-4">
                {kpis.map((k) => (
                    <KpiCard key={k.id} kpi={k} onClick={() => setOpenId(k.id)} />
                ))}
            </div>

            {/* OFFCANVAS DRILLDOWN */}
            <Offcanvas open={openId !== null} kpi={activeKpi} onClose={() => setOpenId(null)} />
        </div>
    );
}

/* ---------------- KPI CARD ---------------- */
function KpiCard({ kpi, onClick }) {
    const Icon = kpi.icon;

    return (
        <button
            onClick={onClick}
            className="
                bg-white
                rounded-2xl
                p-5
                flex
                items-center
                shadow-md
                hover:shadow-xl
                hover:-translate-y-1
                border border-gray-100
                transition-all duration-300
            "
        >
            {/* ICON */}
            <div
                className={`
                    w-14 h-14 
                    rounded-2xl 
                    bg-gradient-to-br ${kpi.color} 
                    flex items-center justify-center 
                    text-white text-2xl shadow-md
                `}
            >
                <Icon />
            </div>

            {/* TEXT */}
            <div className="ml-4 text-left">
                <p className="text-[11px] text-gray-500 font-medium tracking-wide uppercase">
                    {kpi.title}
                </p>

                <p className="text-3xl font-bold text-gray-900 leading-tight mt-1">
                    {kpi.value}
                </p>

                <p className="text-gray-400 text-xs mt-1 font-medium">
                    {kpi.sub}
                </p>
            </div>
        </button>
    );
}

/* ---------------- OFFCANVAS ---------------- */
function Offcanvas({ open, kpi, onClose }) {
    const chartRef = useRef(null);

    useEffect(() => {
        if (!open || !chartRef.current || !kpi) return;
    
        new Chart(chartRef.current, {
            type: "bar",
            data: {
                labels: ["Jun", "Jul", "Aug", "Sept", "Oct", "Nov"],
                datasets: [
                    {
                        label: kpi.title,   // ✅ legend text
                        data: kpi.spark,
                        backgroundColor: "rgba(99, 102, 241, 0.7)",
                        borderRadius: 6,
                    },
                ],
            },
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: true,         // ⭐ ENABLE LEGEND
                        position: "top",
                        labels: {
                            usePointStyle: true,
                            boxWidth: 8,
                            padding: 20,
                        }
                    },
                    tooltip: {
                        enabled: true,
                    }
                },
                scales: {
                    x: {
                        ticks: { color: "#6b7280" },
                        grid: { display: false }
                    },
                    y: {
                        beginAtZero: true,
                        ticks: { color: "#6b7280" },
                        grid: { color: "#f3f4f6" }
                    }
                }
            }
        });
    }, [open, kpi]);
    return (
        <aside
            className={`fixed top-[8vh] right-0 h-[92vh] w-[40vw] bg-white shadow-xl transition-all duration-300 p-6 overflow-y-auto ${open ? "translate-x-0" : "translate-x-full"
                }`}
        >
            {kpi && (
                <>
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-lg font-semibold">{kpi.title}</h2>
                            <p className="text-xs text-gray-400">{kpi.sub}</p>
                        </div>

                        <button
                            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>

                    {/* BAR GRAPH */}
                    <div className="mt-6 bg-gray-50 p-4 rounded-xl">
                        <canvas ref={chartRef} height={200}></canvas>
                    </div>

                    {/* TABLE */}
                    <div className="mt-6">
                        <h3 className="text-sm text-gray-500 mb-2">Breakdown</h3>
                        <table className="w-full text-sm">
                            <tbody>
                                <tr className="border-t">
                                    <td className="py-2">This month</td>
                                    <td className="text-right font-medium">
                                        {kpi.spark[kpi.spark.length - 1]}
                                    </td>
                                </tr>
                                <tr className="border-t">
                                    <td className="py-2">Last month</td>
                                    <td className="text-right">{kpi.spark[kpi.spark.length - 2]}</td>
                                </tr>
                                <tr className="border-t">
                                    <td className="py-2">Avg (3 months)</td>
                                    <td className="text-right">
                                        {(
                                            kpi.spark
                                                .slice(-3)
                                                .reduce((a, b) => a + b, 0) / 3
                                        ).toFixed(1)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </aside>
    );
}
